import type { SegmentModel, SegmentStatusType } from '@/types'
import api from '@/utils/api/api'

type RequestOptions = {
  signal?: AbortSignal
}

export type SegmentsScope = 'mine' | 'shared-with-me'

export type SegmentPreviewItem = Pick<SegmentModel, 'id' | 'description'> & {
  supplierShortModel?: { description: string }
}

export type SegmentsPreview = {
  myAvailableSegments: SegmentPreviewItem[]
  partnerAvailableSegments: SegmentPreviewItem[]
}

export type CreateSegmentRequest = {
  description: string
  baseSegmentId: string | null
  priceModifierPercentage: number
  isDefaultForNewClients: boolean
  markupPercentage: number
  isRrc: boolean
}

export type UpdateSegmentRequest = {
  owner: string
  description: string
  baseSegmentId: string | null
  priceModifierPercentage: number
  markupPercentage: number
  isForClient: boolean
  isActive: boolean
  isRrc?: boolean
}

type SegmentListResponse = { segments: SegmentModel[] }

type SegmentResponse = { segment: SegmentModel }

const SEGMENTS_PATH = '/segments'

export const segmentsApi = {
  getSegments: async (scope: SegmentsScope, options?: RequestOptions) => {
    const { data } = await api.get<SegmentListResponse>(
      `${SEGMENTS_PATH}/${scope}`,
      { signal: options?.signal }
    )
    return data.segments
  },
  getPreview: async (options?: RequestOptions) => {
    const { data } = await api.get<SegmentsPreview>(
      `${SEGMENTS_PATH}/preview`,
      { signal: options?.signal }
    )
    return data
  },
  createSegment: async (payload: CreateSegmentRequest) => {
    const { data } = await api.post<SegmentResponse>(SEGMENTS_PATH, payload)
    return data.segment
  },
  updateSegment: async (id: string, payload: UpdateSegmentRequest) => {
    const { data } = await api.put<SegmentResponse>(
      `${SEGMENTS_PATH}/${id}`,
      payload
    )
    return data.segment
  },
  reorderSegments: async (segmentIds: string[]) => {
    const { data } = await api.patch<SegmentListResponse>(
      `${SEGMENTS_PATH}/order`,
      { segmentIds }
    )
    return data.segments
  },
  changeStatus: async (id: string, status: SegmentStatusType) => {
    const { data } = await api.patch<SegmentResponse>(
      `${SEGMENTS_PATH}/${id}/status`,
      { status }
    )
    return data.segment
  },
}
